import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#e60108', // Bold Red - Energy, Passion, Action
      light: '#ff3339',
      dark: '#b80006',
      contrastText: '#fefefe',
    },
    secondary: {
      main: '#191919', // Dark/Black - Authority, Sophistication, Power
      light: '#333333',
      dark: '#000000',
      contrastText: '#fefefe',
    },
    accent: {
      main: '#a0a0a0', // Gray - Balance, Professionalism, Modern
      light: '#bebebe',
      dark: '#7a7a7a',
      contrastText: '#191919',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
      contrastText: '#fefefe',
    },
    error: {
      main: '#e60108',
      light: '#ff3339',
      dark: '#b80006',
      contrastText: '#fefefe',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
      contrastText: '#191919',
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#2563EB',
      contrastText: '#fefefe',
    },
    background: {
      default: '#fefefe',
      paper: '#fefefe',
      dark: '#191919',
      gray: '#a0a0a0',
      gradient: 'linear-gradient(135deg, #e60108 0%, #191919 100%)',
      darkGradient: 'linear-gradient(135deg, #191919 0%, #333333 100%)',
      accentGradient: 'linear-gradient(135deg, #a0a0a0 0%, #e60108 100%)',
      redGradient: 'linear-gradient(135deg, #e60108 0%, #ff3339 100%)',
    },
    text: {
      primary: '#191919',
      secondary: '#a0a0a0',
      disabled: '#c0c0c0',
      hint: '#d4d4d4',
      light: '#fefefe',
    },
    divider: '#e5e5e5',
    action: {
      hover: 'rgba(230, 1, 8, 0.04)',
      selected: 'rgba(230, 1, 8, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      active: '#e60108',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Poppins', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
    h1: {
      fontSize: '4rem',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: '#191919',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontSize: '3rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: '#191919',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: '#191919',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#191919',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#191919',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.6,
      color: '#191919',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#4a4a4a',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#a0a0a0',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.3px',
    },
    caption: {
      color: '#a0a0a0',
      fontSize: '0.75rem',
    },
    overline: {
      color: '#e60108',
      fontWeight: 600,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0,0,0,0.05)',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.05)',
    '0px 6px 12px rgba(0,0,0,0.05)',
    '0px 8px 16px rgba(0,0,0,0.05)',
    '0px 10px 20px rgba(0,0,0,0.05)',
    '0px 12px 24px rgba(0,0,0,0.1)',
    '0px 16px 32px rgba(0,0,0,0.1)',
    '0px 20px 40px rgba(0,0,0,0.1)',
    '0px 24px 48px rgba(230, 1, 8, 0.15)',
    '0px 28px 56px rgba(230, 1, 8, 0.15)',
    '0px 32px 64px rgba(230, 1, 8, 0.15)',
    '0px 36px 72px rgba(230, 1, 8, 0.15)',
    '0px 40px 80px rgba(230, 1, 8, 0.15)',
    '0px 44px 88px rgba(230, 1, 8, 0.15)',
    '0px 48px 96px rgba(230, 1, 8, 0.15)',
    '0px 52px 104px rgba(230, 1, 8, 0.15)',
    '0px 56px 112px rgba(230, 1, 8, 0.15)',
    '0px 60px 120px rgba(230, 1, 8, 0.15)',
    '0px 64px 128px rgba(230, 1, 8, 0.15)',
    '0px 68px 136px rgba(230, 1, 8, 0.15)',
    '0px 72px 144px rgba(230, 1, 8, 0.15)',
    '0px 76px 152px rgba(230, 1, 8, 0.15)',
    '0px 80px 160px rgba(230, 1, 8, 0.15)',
  ],
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#e60108 #f5f5f5',
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5',
            borderRadius: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e60108',
            borderRadius: '5px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#b80006',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.9375rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #e60108 0%, #ff3339 100%)',
          boxShadow: '0 4px 14px rgba(230, 1, 8, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #cc0006 0%, #e60108 100%)',
            boxShadow: '0 6px 20px rgba(230, 1, 8, 0.4)',
          },
        },
        containedSecondary: {
          background: '#191919',
          boxShadow: '0 4px 14px rgba(25, 25, 25, 0.25)',
          '&:hover': {
            background: '#000000',
            boxShadow: '0 6px 20px rgba(25, 25, 25, 0.35)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
        outlinedPrimary: {
          borderColor: '#e60108',
          color: '#e60108',
          '&:hover': {
            borderColor: '#b80006',
            backgroundColor: 'rgba(230, 1, 8, 0.04)',
          },
        },
        textPrimary: {
          color: '#e60108',
          '&:hover': {
            backgroundColor: 'rgba(230, 1, 8, 0.04)',
          },
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(254, 254, 254, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (min-width: 600px)': {
            paddingLeft: 32,
            paddingRight: 32,
          },
        },
        maxWidthXl: {
          maxWidth: 1400,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.3s ease',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e60108',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e60108',
                borderWidth: 2,
              },
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#e60108',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 16,
        },
        elevation: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: '#e60108',
          color: '#fefefe',
        },
        outlined: {
          borderColor: '#e60108',
          color: '#e60108',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#e5e5e5',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '0',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: '#f0f0f0',
        },
        bar: {
          backgroundColor: '#e60108',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': {
            color: '#e60108',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#e60108',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#e60108',
          textDecoration: 'none',
          '&:hover': {
            color: '#b80006',
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(230, 1, 8, 0.04)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(230, 1, 8, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(230, 1, 8, 0.12)',
            },
          },
        },
      },
    },
  },
});

export default theme;